<template>
  <div
    v-if="open"
    class="rl-sheet"
    role="dialog"
    aria-modal="true"
    aria-labelledby="rl-sheet-title"
    @keydown.esc.prevent="close"
  >
    <button
      type="button"
      class="rl-sheet__backdrop"
      aria-label="Close"
      :style="{ top: panelTopPx + 'px' }"
      @click="close"
    />
    <!--
      Mockup install-banner: dark panel floats under the branded phone nav
      (logo + accountexec + SRM stay visible). Opt out = ✕ only; Add = cyan button.
    -->
    <div
      class="rl-sheet__panel"
      role="document"
      :style="{ top: panelTopPx + 'px' }"
    >
      <button
        type="button"
        class="rl-sheet__x"
        aria-label="Decline"
        @click="close"
      >✕</button>

      <div class="rl-sheet__body">
        <img
          class="rl-sheet__tile"
          :src="tileSrc"
          alt=""
          width="40"
          height="40"
        >
        <div class="rl-sheet__text">
          <h2 id="rl-sheet-title" class="rl-sheet__title">
            Add to your Home Screen
          </h2>
          <p class="rl-sheet__sub">Full screen, faster, no browser bar!</p>
        </div>
      </div>

      <div class="rl-sheet__actions">
        <button
          type="button"
          class="rl-sheet__add"
          :disabled="busy"
          @click.stop.prevent="onAdd"
        >{{ busy ? '…' : 'Add' }}</button>
      </div>

      <p v-if="statusText" class="rl-sheet__status" role="status">{{ statusText }}</p>
    </div>
  </div>
</template>

<script>
/**
 * Rocket Launcher confirm — dark install banner from the homescreen mockup.
 * Tile: /icons/icon-192.png. Opt out = ✕ (or backdrop); opt in = Add.
 */
import {
  isIosLike,
  openIosShortcut,
  runInstallAction,
} from './homeScreenInstall'
import {
  isLocalDevHost,
  noteRocketLauncherSheetDismissed,
  shouldAutoOpenRocketLauncherSheet,
  subscribeRocketLauncherSheet,
} from './rocketLauncherSheetBus'
import { HOME_SCREEN_TILE_SRC } from './homeScreenTile'

export default {
  name: 'RocketLauncherSheet',
  data () {
    return {
      open: false,
      busy: false,
      statusText: '',
      unsubBus: null,
      tileSrc: HOME_SCREEN_TILE_SRC,
      /** Pixels from viewport top — sits just under the branded phone banner. */
      panelTopPx: 80,
    }
  },
  mounted () {
    this.unsubBus = subscribeRocketLauncherSheet(() => {
      this.show()
    })
    if (shouldAutoOpenRocketLauncherSheet()) {
      this.$nextTick(() => { this.show() })
    }
  },
  beforeDestroy () {
    if (typeof this.unsubBus === 'function') this.unsubBus()
  },
  methods: {
    placeBelowBanner () {
      // Clear the full fixed phone app bar + brand lockup (logo / wordmark / SRM).
      let bottom = 0
      const nodes = document.querySelectorAll(
        '.nav-app-bar--phone, .nav-app-bar.v-app-bar--fixed, .nav-bar-row--phone, .nav-brand-phone'
      )
      nodes.forEach((el) => {
        const b = el.getBoundingClientRect().bottom
        if (b > bottom) bottom = b
      })
      if (bottom > 0) {
        this.panelTopPx = Math.max(120, Math.round(bottom + 20))
        return
      }
      this.panelTopPx = 120
    },
    show () {
      this.statusText = ''
      this.placeBelowBanner()
      this.open = true
      this.$nextTick(() => {
        this.placeBelowBanner()
        requestAnimationFrame(() => this.placeBelowBanner())
        const btn = this.$el && this.$el.querySelector('.rl-sheet__add')
        if (btn) btn.focus()
      })
    },
    close () {
      if (this.busy) return
      noteRocketLauncherSheetDismissed()
      this.open = false
      this.statusText = ''
    },
    /**
     * iOS must navigate in the same synchronous turn as the tap.
     * Any await (even an async wrapper) breaks the user-gesture chain and
     * Chrome/Safari will ignore location.href — looks like a dead Add button.
     */
    onAdd () {
      if (this.busy) return
      this.statusText = ''
      // Sync navigate on iOS — await breaks the tap gesture and Chrome ignores href.
      if (isIosLike()) {
        const result = openIosShortcut()
        if (result.ok) {
          this.statusText = 'Opening Shortcuts…'
          return
        }
        this.statusText = 'Shortcut link missing. Check Home Screen Shortcut config.'
        return
      }
      this.onAddAsync()
    },
    async onAddAsync () {
      if (this.busy) return
      this.busy = true
      this.statusText = ''
      try {
        const result = await runInstallAction()
        if (isLocalDevHost() && (result.path === 'unsupported' || result.outcome === 'unavailable')) {
          this.busy = false
          this.close()
          return
        }
        if (result.path === 'native' && result.outcome === 'accepted') {
          this.statusText = 'Added — look for Accountexec on your Home Screen.'
          setTimeout(() => {
            this.busy = false
            noteRocketLauncherSheetDismissed()
            this.open = false
          }, 900)
          return
        }
        if (result.path === 'native' && result.outcome === 'dismissed') {
          this.statusText = 'Install dismissed. You can try again anytime.'
        } else if (result.path === 'ios-shortcut' && result.ok) {
          this.statusText = 'Shortcuts opened — Add Shortcut, then Run to finish.'
        } else if (result.path === 'ios-shortcut' && !result.ok) {
          this.statusText = 'Shortcut link missing. Check Home Screen Shortcut config.'
        } else if (result.path === 'unsupported' || result.outcome === 'unavailable') {
          this.statusText = isIosLike()
            ? 'Shortcut link missing. Check Home Screen Shortcut config.'
            : 'Install not ready yet. Use Chrome on Android, or open Accountexec on an iPhone.'
        } else if (result.path === 'installed') {
          this.statusText = 'Already on your Home Screen.'
          setTimeout(() => {
            this.busy = false
            noteRocketLauncherSheetDismissed()
            this.open = false
          }, 700)
          return
        }
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.rl-sheet {
  position: fixed;
  inset: 0;
  /* Above Vuetify temporary drawer/overlay so Add stays tappable after hamburger */
  z-index: 10000;
  box-sizing: border-box;
  pointer-events: none;
}
/* Dim only below the brand banner — logo / wordmark / SRM stay undimmed. */
.rl-sheet__backdrop {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  border: none;
  padding: 0;
  margin: 0;
  background: rgba(15, 20, 30, 0.28);
  cursor: pointer;
  pointer-events: auto;
}
.rl-sheet__panel {
  position: absolute;
  left: 14px;
  right: 14px;
  z-index: 1;
  width: auto;
  max-width: 420px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #fff;
  background: linear-gradient(135deg, #0c1e2e, #123349);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  /* Compact (~½ prior height): tile+copy row + full-width Add */
  padding: 10px 12px;
  box-sizing: border-box;
  pointer-events: auto;
}
.rl-sheet__x {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #8ba7b8;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
  z-index: 2;
}
.rl-sheet__body {
  display: flex;
  align-items: center; /* title + tagline vertically centered to ae tile */
  gap: 10px;
  min-width: 0;
  padding-right: 28px; /* clear ✕ */
}
.rl-sheet__tile {
  display: block;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  object-fit: contain;
  background: transparent;
}
.rl-sheet__text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.rl-sheet__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}
.rl-sheet__sub {
  margin: 2px 0 0;
  font-size: 11px;
  color: #9fc9de;
  line-height: 1.25;
}
.rl-sheet__actions {
  display: block;
  width: 100%;
}
/* Add stretches left–right across the panel bottom */
.rl-sheet__add {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: #00b0f0 !important;
  color: #062431 !important;
  border: none !important;
  border-radius: 8px;
  padding: 8px 12px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.01em;
  min-height: 36px;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  box-shadow: none;
  box-sizing: border-box;
}
.rl-sheet__add:disabled {
  opacity: 0.55;
  cursor: default;
}
.rl-sheet__status {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.25;
  color: #9fc9de;
}
</style>
